import { useCallback, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import type { PlayerError, PlayerState } from '../../types';

// AWS IVS Player SDK の型定義
declare global {
  interface Window {
    IVSPlayer?: {
      create: () => IVSPlayerInstance;
      isPlayerSupported: boolean;
      PlayerEventType: {
        ERROR: string;
        PLAYING?: string;
        ENDED?: string;
        BUFFERING?: string;
        IDLE?: string;
      };
      PlayerState?: {
        IDLE: string;
        READY: string;
        BUFFERING: string;
        PLAYING: string;
        ENDED: string;
      };
    };
  }
}

interface IVSPlayerInstance {
  attachHTMLVideoElement: (video: HTMLVideoElement) => void;
  load: (url: string) => void;
  play: () => Promise<void> | void;
  pause: () => void;
  addEventListener: (event: string, callback: (error?: any) => void) => void;
  removeEventListener: (event: string, callback: (error?: any) => void) => void;
  setRebufferToLive?: (enabled: boolean) => void;
  /** プレイヤーが確保したリソース（WASMワーカー・バッファ等）を解放する */
  delete?: () => void;
}

/**
 * IVS プレイヤーインスタンスを破棄する（S6対応）
 * pause だけではSDK内部のワーカー・バッファが解放されずリークするため、
 * イベントリスナーを外したうえで delete() を呼んでリソースを解放する。
 */
function destroyIVSPlayer(player: IVSPlayerInstance): void {
  try {
    const listeners = (player as any)._listeners as Array<[string, (e?: any) => void]> | undefined;
    if (listeners) {
      for (const [eventName, handler] of listeners) {
        try {
          player.removeEventListener(eventName, handler);
        } catch {
          /* noop */
        }
      }
    }
    player.pause();
    if (typeof player.delete === 'function') {
      player.delete();
    }
  } catch (e) {
    // noop
  }
}

/** S3 / WebSocket 同期の講義ライブ状態。接続待ちオーバーレイの文言切り替えに使用 */
export type LectureLiveStatus = 'ready' | 'running' | 'ended';

export interface IVSPlayerProps {
  m3u8Url: string;
  autoplay?: boolean;
  lectureLiveStatus?: LectureLiveStatus;
  onStateChange?: (state: PlayerState) => void;
  onError?: (error: PlayerError) => void;
  onOverlayChange?: (show: boolean, title: string, subtitle: string, showRetry: boolean) => void;
}

export interface IVSPlayerHandle {
  getVideoElement: () => HTMLVideoElement | null;
  seekTo: (seconds: number) => void;
}

type StreamStatus = 'not_started' | 'live' | 'interrupted' | 'ended' | 'error';

/** 再生中に currentTime がこの時間進まなかったら配信中断とみなす（ストールウォッチドッグ） */
const STALL_TIMEOUT_MS = 10000;
/**
 * ストール判定のベースラインを更新する最小進行秒数。
 * 配信断直後はバッファ末尾の微小なにじみ進行（数秒に0.1秒程度）が発生し、単純な
 * 「変化あり=正常」判定だとタイマーがリセットされ続けて検知が大幅に遅れるため、
 * この秒数以上進んだ場合のみ正常再生とみなす
 */
const STALL_MIN_PROGRESS_SEC = 0.5;
/** ストールウォッチドッグの監視間隔 */
const STALL_CHECK_INTERVAL_MS = 2000;
/**
 * 中断からの復旧観察期間。
 * 配信断直後は CDN に古いプレイリストが残っており、再接続すると残存セグメント（数秒分）だけ
 * 再生されて playing が発火する。これを復旧と誤認すると蓋絵が消える→また止まるのフラッピングに
 * なるため、この期間 currentTime が進み続けた場合のみ復旧確定とする。
 */
const RECOVERY_PROBATION_MS = 5000;
/** 復旧観察期間中に最低この秒数だけ再生が進んだら復旧確定 */
const RECOVERY_MIN_PROGRESS_SEC = 3;

function getPrePlayErrorOverlayCopy(status: LectureLiveStatus | undefined): { title: string; subtitle: string } {
  if (status === 'ended') {
    return {
      title: '配信が終了しました',
      subtitle: '配信が終了した可能性があります。',
    };
  }
  if (status === 'running') {
    return {
      title: '配信を接続できませんでした',
      subtitle: 'しばらくしてから「再試行」を押すか、ページを再読み込みしてください。',
    };
  }
  return {
    title: 'ライブ開始までお待ちください',
    subtitle: '配信開始後に自動で接続を試みます。',
  };
}

/** 講義開始ステータスで配信映像準備中のオーバーレイ文言 */
function getPendingOverlayCopy(status: LectureLiveStatus | undefined): { title: string; subtitle: string } {
  if (status === 'ended') {
    return {
      title: '配信が終了しました',
      subtitle: 'ライブ配信は終了しています。',
    };
  }
  if (status === 'running') {
    return {
      title: '配信を接続しています',
      subtitle: '接続までしばらくお待ちください。',
    };
  }
  return {
    title: 'ライブ開始までお待ちください',
    subtitle: '講義が開始されると、配信への接続を自動で行います。',
  };
}

/**
 * IVSPlayer - AWS IVS Player SDK integration with native HLS fallback.
 */
export const IVSPlayer = forwardRef<IVSPlayerHandle, IVSPlayerProps>(({
  m3u8Url,
  autoplay = false,
  lectureLiveStatus,
  onStateChange,
  onError,
  onOverlayChange,
}, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const lectureLiveStatusRef = useRef(lectureLiveStatus);
  lectureLiveStatusRef.current = lectureLiveStatus;

  useImperativeHandle(ref, () => ({
    getVideoElement: () => videoRef.current,
    seekTo: (seconds: number) => {
      const video = videoRef.current;
      if (!video || !Number.isFinite(seconds) || seconds < 0) return;
      try {
        video.currentTime = seconds;
        if (video.paused) video.play().catch(() => {});
      } catch { /* ignore */ }
    },
  }));
  const playerRef = useRef<IVSPlayerInstance | null>(null);
  const [streamStatus, setStreamStatus] = useState<StreamStatus>('not_started');
  // streamStatus をコールバック・effect 内で参照するための ref（deps に入れると無限ループになるため）
  const streamStatusRef = useRef<StreamStatus>('not_started');
  streamStatusRef.current = streamStatus;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // hasEverPlayed is used in setHasEverPlayed callbacks to track playback state
  const [_hasEverPlayed, setHasEverPlayed] = useState(false);
  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const retryDelayMsRef = useRef<number>(2500);
  /** 現在のプレイヤーに対する再接続関数（load + play）。ウォッチドッグ等から呼ぶ */
  const attemptStartRef = useRef<(() => void) | null>(null);
  /** ストールウォッチドッグ用: 最後に currentTime が進んだ時刻 */
  const lastProgressRef = useRef<{ time: number; at: number } | null>(null);
  /** 中断からの復旧観察タイマー */
  const recoveryProbationTimerRef = useRef<NodeJS.Timeout | null>(null);

  const updateState = useCallback(
    (state: PlayerState) => {
      onStateChange?.(state);
    },
    [onStateChange],
  );

  const handleError = useCallback(
    (code: string, message: string) => {
      const error: PlayerError = { code, message };
      setErrorMessage(message);
      setStreamStatus('error');
      updateState('error');
      onError?.(error);
      console.error(`[IVSPlayer] Error ${code}: ${message}`);
    },
    [onError, updateState],
  );

  const clearRetry = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const showOverlay = useCallback(
    (title: string, subtitle: string, showRetry: boolean) => {
      onOverlayChange?.(true, title, subtitle, showRetry);
    },
    [onOverlayChange],
  );

  const hideOverlay = useCallback(() => {
    onOverlayChange?.(false, '', '', false);
  }, [onOverlayChange]);

  const scheduleRetry = useCallback(
    (attemptStart: () => void) => {
      clearRetry();
      // ±15%のジッタを付与し、配信断→復旧時に全視聴者の再接続が一斉集中しないようにする
      const jitteredDelay = Math.round(retryDelayMsRef.current * (0.85 + Math.random() * 0.3));
      retryTimerRef.current = setTimeout(() => {
        retryDelayMsRef.current = Math.min(10000, Math.round(retryDelayMsRef.current * 1.3));
        attemptStart();
      }, jitteredDelay);
    },
    [clearRetry],
  );

  const clearRecoveryProbation = useCallback(() => {
    if (recoveryProbationTimerRef.current) {
      clearTimeout(recoveryProbationTimerRef.current);
      recoveryProbationTimerRef.current = null;
    }
  }, []);

  /**
   * 中断状態で playing が発火したときに呼ぶ。
   * すぐに復旧扱いにせず、RECOVERY_PROBATION_MS の間 currentTime が進み続けたら復旧確定して
   * 蓋絵を外す。残存セグメントの一時的な再生（数秒で再び停止する）は復旧と見なさず、
   * 蓋絵を出したまま再接続を継続する。
   */
  const startRecoveryProbation = useCallback(() => {
    if (recoveryProbationTimerRef.current) return; // 観察中
    const startCt = videoRef.current?.currentTime ?? 0;
    recoveryProbationTimerRef.current = setTimeout(() => {
      recoveryProbationTimerRef.current = null;
      if (streamStatusRef.current !== 'interrupted') return;
      if (lectureLiveStatusRef.current !== 'running') return;
      const video = videoRef.current;
      const advanced = video ? video.currentTime - startCt : 0;
      if (video && !video.paused && advanced >= RECOVERY_MIN_PROGRESS_SEC) {
        // 復旧確定：蓋絵を外してバックオフをリセット
        setStreamStatus('live');
        retryDelayMsRef.current = 2500;
        clearRetry();
        updateState('playing');
      } else {
        // 残存セグメントの再生だった：中断のまま再接続を継続
        scheduleRetry(() => attemptStartRef.current?.());
      }
    }, RECOVERY_PROBATION_MS);
  }, [clearRetry, scheduleRetry, updateState]);

  const safePlayVideoEl = useCallback((video: HTMLVideoElement) => {
    try {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (e) {
      // noop
    }
  }, []);

  const initPlayer = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    // Clean up previous instance
    // pause だけでなく delete() でSDK内部リソースも解放する（S6対応）
    if (playerRef.current) {
      destroyIVSPlayer(playerRef.current);
      playerRef.current = null;
    }

    clearRetry();
    clearRecoveryProbation();
    attemptStartRef.current = null;
    lastProgressRef.current = null;
    setErrorMessage(null);
    setStreamStatus('not_started');
    setHasEverPlayed(false);
    retryDelayMsRef.current = 2500;
    updateState('loading');

    const playbackUrl = m3u8Url?.trim() || '';

    if (!playbackUrl || playbackUrl === 'https://YOUR_PLAYBACK_URL.m3u8') {
      console.error('[IVS] playbackUrl が未設定です。');
      return;
    }

    // AWS IVS Player SDK を使用
    if (window.IVSPlayer && window.IVSPlayer.isPlayerSupported) {
      const ivs = window.IVSPlayer;

      const initialConnecting = getPendingOverlayCopy(lectureLiveStatusRef.current);
      showOverlay(initialConnecting.title, initialConnecting.subtitle, true);

      // 配信の切断・終了を検知したときの共通処理（ERROR / ENDED 状態の両方から呼ぶ）
      // hasEverPlayedの現在の値を取得するため、setHasEverPlayedのコールバック形式を使用
      const handleStreamDrop = () => {
        setHasEverPlayed((currentHasEverPlayed) => {
          if (!currentHasEverPlayed) {
            const { title, subtitle } = getPrePlayErrorOverlayCopy(lectureLiveStatusRef.current);
            showOverlay(title, subtitle, true);
            if (lectureLiveStatusRef.current !== 'ended') {
              scheduleRetry(() => attemptStartRef.current?.());
            }
          } else if (lectureLiveStatusRef.current === 'ended') {
            // 講義終了後の切断は「終了」としてオーバーレイを表示
            setStreamStatus('ended');
            updateState('ended');
          } else {
            // 講義継続中の切断は「中断」としてオーバーレイを表示し、自動再接続を続ける
            setStreamStatus('interrupted');
            updateState('error');
            scheduleRetry(() => attemptStartRef.current?.());
          }
          return currentHasEverPlayed;
        });
      };

      const errorHandler = (err: any) => {
        console.error('[IVS] Player error:', err);
        handleStreamDrop();
      };

      // OBS の配信停止では ERROR ではなく ENDED 状態遷移になることが多いため両方を購読する
      const endedStateHandler = () => {
        console.warn('[IVS] Stream ended (player state ENDED)');
        handleStreamDrop();
      };

      // 再生開始イベント
      const playingHandler = () => {
        // 講義前(ready)または講義終了(ended)の場合は再生をブロック
        if (
          lectureLiveStatusRef.current === 'ended' ||
          lectureLiveStatusRef.current === 'ready'
        ) {
          try {
            playerRef.current?.pause();
          } catch {
            /* ignore */
          }
          return;
        }
        setHasEverPlayed(true);
        // 中断からの playing は残存セグメント再生の可能性があるため、即復旧とせず観察期間を挟む
        if (streamStatusRef.current === 'interrupted') {
          startRecoveryProbation();
          return;
        }
        setStreamStatus('live');
        retryDelayMsRef.current = 2500;
        clearRetry();
        hideOverlay();
        updateState('playing');
      };

      // 接続試行。エラー発生後のインスタンスは load() を再実行しても「バッファは増えるが
      // 再生位置が進まない」壊れた状態になることがあるため（実配信の経過観察で確認）、
      // 試行のたびに旧インスタンスを破棄して新規作成する（IVS 推奨プラクティス）
      const attemptStart = () => {
        clearRetry();
        if (playerRef.current) {
          destroyIVSPlayer(playerRef.current);
          playerRef.current = null;
        }
        try {
          const player = ivs.create();
          player.attachHTMLVideoElement(video);
          player.setRebufferToLive?.(true);

          // 実SDKでは PLAYING / ENDED は PlayerState 側の定数（PlayerEventType には存在しない）
          const playingEvent = ivs.PlayerState?.PLAYING ?? ivs.PlayerEventType.PLAYING;
          const endedEvent = ivs.PlayerState?.ENDED ?? ivs.PlayerEventType.ENDED;
          const listeners: Array<[string, (e?: any) => void]> = [
            [ivs.PlayerEventType.ERROR, errorHandler],
          ];
          if (playingEvent) listeners.push([playingEvent, playingHandler]);
          if (endedEvent) listeners.push([endedEvent, endedStateHandler]);
          for (const [eventName, handler] of listeners) {
            player.addEventListener(eventName, handler);
          }
          // クリーンアップ用に購読リストを保存
          (player as any)._listeners = listeners;
          playerRef.current = player;

          try {
            player.load(playbackUrl);
          } catch (e) {
            console.error('[IVS] load error:', e);
          }
          try {
            const maybePromise = player.play();
            if (maybePromise && typeof maybePromise.catch === 'function') {
              maybePromise.catch(() => {});
            }
          } catch (e) {
            console.error('[IVS] play error:', e);
          }
        } catch (e) {
          console.error('[IVS] Player creation error:', e);
          handleError('PLAYER_CREATION_ERROR', 'プレイヤーの初期化に失敗しました。');
        }
      };
      attemptStartRef.current = attemptStart;

      attemptStart();
    } else {
      // フォールバック（ネイティブHLS想定）
      console.warn('[IVS] AWS IVS Player SDK not available, using native HLS fallback');
      const fallbackAttemptStart = () => {
        clearRetry();
        video.src = playbackUrl;
        safePlayVideoEl(video);
      };
      attemptStartRef.current = fallbackAttemptStart;
      fallbackAttemptStart();
      const fallbackConnecting = getPendingOverlayCopy(lectureLiveStatusRef.current);
      showOverlay(fallbackConnecting.title, fallbackConnecting.subtitle, true);

      const videoErrorHandler = () => {
        setHasEverPlayed((currentHasEverPlayed) => {
          if (!currentHasEverPlayed) {
            const { title, subtitle } = getPrePlayErrorOverlayCopy(lectureLiveStatusRef.current);
            showOverlay(title, subtitle, true);
            if (lectureLiveStatusRef.current !== 'ended') {
              scheduleRetry(fallbackAttemptStart);
            }
          } else if (lectureLiveStatusRef.current === 'ended') {
            // 講義終了後の切断は「終了」としてオーバーレイを表示
            setStreamStatus('ended');
            updateState('ended');
          } else {
            // 講義継続中の切断は「中断」としてオーバーレイを表示し、自動再接続を続ける
            setStreamStatus('interrupted');
            updateState('error');
            scheduleRetry(fallbackAttemptStart);
          }
          return currentHasEverPlayed;
        });
      };

      const videoPlayingHandler = () => {
        // 講義前(ready)または講義終了(ended)の場合は再生をブロック
        if (
          lectureLiveStatusRef.current === 'ended' ||
          lectureLiveStatusRef.current === 'ready'
        ) {
          try {
            video.pause();
          } catch {
            /* ignore */
          }
          return;
        }
        setHasEverPlayed(true);
        // 中断からの playing は残存セグメント再生の可能性があるため、即復旧とせず観察期間を挟む
        if (streamStatusRef.current === 'interrupted') {
          startRecoveryProbation();
          return;
        }
        setStreamStatus('live');
        retryDelayMsRef.current = 2500;
        clearRetry();
        hideOverlay();
        updateState('playing');
      };

      video.addEventListener('error', videoErrorHandler);
      video.addEventListener('playing', videoPlayingHandler);

      // クリーンアップ関数を保存
      (video as any)._errorHandler = videoErrorHandler;
      (video as any)._playingHandler = videoPlayingHandler;
    }
    }, [m3u8Url, autoplay, updateState, handleError, showOverlay, hideOverlay, clearRetry, clearRecoveryProbation, scheduleRetry, startRecoveryProbation, safePlayVideoEl]);

  // Initialize player on mount / URL change only (initPlayer は onStateChange/onOverlayChange に依存するため
  // 依存に含めると親の再レンダーで毎回 effect が走り無限ループになる)
  useEffect(() => {
    initPlayer();

    return () => {
      clearRetry();
      clearRecoveryProbation();
      const player = playerRef.current;
      if (player) {
        // イベントリスナー除去 + pause + delete() でリソースを解放する（S6対応）
        destroyIVSPlayer(player);
        playerRef.current = null;
      }
      const video = videoRef.current;
      if (video) {
        const errorHandler = (video as any)._errorHandler;
        const playingHandler = (video as any)._playingHandler;
        if (errorHandler) {
          video.removeEventListener('error', errorHandler);
        }
        if (playingHandler) {
          video.removeEventListener('playing', playingHandler);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initPlayer を deps に含めると親のコールバック更新で無限ループになるため、URL のみで制御
  }, [m3u8Url, autoplay]);

  // lectureLiveStatus の遷移に応じてプレイヤーを再起動・リセットする。
  //
  // 対応パターン:
  //   ready/ended → running : バックオフをリセットして即時リトライ
  //                           ended からの場合は streamStatus もリセット
  //   ended/running → ready : streamStatus をリセットして待機オーバーレイを表示
  //                           プレイヤーを停止し再接続を試みる
  //
  // ※ running → ended は下の専用 useEffect で処理する
  const prevLectureLiveStatusRef = useRef<LectureLiveStatus | undefined>(undefined);
  const m3u8UrlRef = useRef(m3u8Url);
  m3u8UrlRef.current = m3u8Url;

  useEffect(() => {
    const prev = prevLectureLiveStatusRef.current;
    prevLectureLiveStatusRef.current = lectureLiveStatus;

    // ─── running への遷移 ────────────────────────────────────────────
    if (lectureLiveStatus === 'running' && prev !== 'running') {
      clearRetry();
      retryDelayMsRef.current = 2500;

      // ended → running : streamStatus が ended のままになるためリセット必須
      if (prev === 'ended') {
        setStreamStatus('not_started');
        setHasEverPlayed(false);
        setErrorMessage(null);
      }

      const url = m3u8UrlRef.current?.trim();
      if (!url || url === 'https://YOUR_PLAYBACK_URL.m3u8') return;

      const player = playerRef.current;
      if (player) {
        try { player.load(url); } catch { /* noop */ }
        try {
          const p = player.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch { /* noop */ }
      } else {
        const video = videoRef.current;
        if (video && !window.IVSPlayer?.isPlayerSupported) {
          video.src = url;
          try {
            const p = video.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          } catch { /* noop */ }
        }
      }
      return;
    }

    // ─── ready への遷移 ──────────────────────────────────────────────
    // ended → ready : streamStatus が ended のままになるためリセット必須
    // running → ready（配信中）: streamStatus が live のままになり蓋が出ないためリセット必須
    // running → ready（接続待ち）: streamStatus は not_started/error のまま。
    //                              オーバーレイ文言は lectureLiveStatus から reactively 更新されるため追加処理不要
    if (
      lectureLiveStatus === 'ready' &&
      prev !== 'ready' &&
      prev !== undefined &&
      (prev === 'ended' || streamStatusRef.current === 'live' || streamStatusRef.current === 'interrupted')
    ) {
      clearRetry();
      clearRecoveryProbation();
      retryDelayMsRef.current = 2500;
      setStreamStatus('not_started');
      setHasEverPlayed(false);
      setErrorMessage(null);

      // 現在のプレイヤーを停止してから再接続を試みる
      // （ストリームが利用可能になったら自動再生、利用不可ならリトライループが継続）
      try { playerRef.current?.pause(); } catch { /* noop */ }
      try { videoRef.current?.pause(); } catch { /* noop */ }

      const url = m3u8UrlRef.current?.trim();
      if (!url || url === 'https://YOUR_PLAYBACK_URL.m3u8') return;

      const player = playerRef.current;
      if (player) {
        try { player.load(url); } catch { /* noop */ }
        try {
          const p = player.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch { /* noop */ }
      } else {
        const video = videoRef.current;
        if (video && !window.IVSPlayer?.isPlayerSupported) {
          video.src = url;
          try {
            const p = video.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          } catch { /* noop */ }
        }
      }
    }
  }, [lectureLiveStatus, clearRetry, clearRecoveryProbation]);

  // 講義ステータスが ended（WebSocket 等）になったら、再生中でも停止して終了オーバーレイを出す
  useEffect(() => {
    if (lectureLiveStatus !== 'ended') return;

    clearRetry();
    clearRecoveryProbation();
    setStreamStatus((prev) => {
      if (prev === 'live' || prev === 'interrupted' || prev === 'not_started' || prev === 'error') {
        return 'ended';
      }
      return prev;
    });
    updateState('ended');

    try {
      playerRef.current?.pause();
    } catch {
      /* ignore */
    }
    const videoEl = videoRef.current;
    if (videoEl) {
      try {
        videoEl.pause();
      } catch {
        /* ignore */
      }
    }
  }, [lectureLiveStatus, clearRetry, clearRecoveryProbation, updateState]);

  // Video element event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => updateState('playing');
    const onPause = () => updateState('paused');
    const onEnded = () => {
      if (lectureLiveStatusRef.current === 'ended') {
        setStreamStatus('ended');
        updateState('ended');
        return;
      }
      // 講義継続中のストリーム終端は「中断」として蓋絵を表示し、自動再接続を試みる
      setStreamStatus('interrupted');
      updateState('error');
      scheduleRetry(() => attemptStartRef.current?.());
    };
    const onWaiting = () => updateState('loading');
    const onPlaying = () => {
      // 講義前(ready)または講義終了(ended)の場合は状態を live に更新しない
      if (
        lectureLiveStatusRef.current === 'ended' ||
        lectureLiveStatusRef.current === 'ready'
      ) return;
      setHasEverPlayed(true);
      // 中断からの playing は残存セグメント再生の可能性があるため、即復旧とせず観察期間を挟む
      if (streamStatusRef.current === 'interrupted') {
        startRecoveryProbation();
        return;
      }
      setStreamStatus('live');
      // 復旧確定：バックオフをリセットし、予約済みの再接続を解除する
      retryDelayMsRef.current = 2500;
      clearRetry();
      updateState('playing');
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('playing', onPlaying);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('playing', onPlaying);
    };
  }, [updateState, scheduleRetry, clearRetry, startRecoveryProbation]);

  // ストールウォッチドッグ:
  // エラーイベントが発火しない切断（回線断・OBS異常終了で映像データだけが途絶するケース）を
  // 検知するため、再生中に currentTime が STALL_TIMEOUT_MS 進まなかったら「中断」とみなして
  // 蓋絵を表示し、再接続を試みる。ユーザーによる一時停止中は判定しない。
  useEffect(() => {
    const timerId = setInterval(() => {
      const video = videoRef.current;
      if (!video) return;
      if (streamStatusRef.current !== 'live' || lectureLiveStatusRef.current !== 'running') {
        lastProgressRef.current = null;
        return;
      }
      if (video.ended) {
        lastProgressRef.current = null;
        return;
      }
      // paused でも readyState >= HAVE_FUTURE_DATA(3) ならユーザーによる一時停止とみなして除外。
      // 一方、データ枯渇状態（readyState < 3）での paused は IVS SDK がバッファ枯渇時に
      // 自動停止したものなので、ストールとして計測を続行する
      if (video.paused && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        lastProgressRef.current = null;
        return;
      }
      const now = Date.now();
      const currentTime = video.currentTime;
      const last = lastProgressRef.current;
      // STALL_MIN_PROGRESS_SEC 以上の進行のみ正常再生とみなしてベースラインを更新する。
      // currentTime が巻き戻った場合（load() によるタイムラインリセット）も更新する
      if (
        !last ||
        currentTime - last.time >= STALL_MIN_PROGRESS_SEC ||
        currentTime < last.time
      ) {
        lastProgressRef.current = { time: currentTime, at: now };
        return;
      }
      if (now - last.at >= STALL_TIMEOUT_MS) {
        console.warn('[IVS] Playback stalled for', now - last.at, 'ms — treating as interruption');
        lastProgressRef.current = null;
        setStreamStatus('interrupted');
        updateState('error');
        // バックオフ付きで再接続を試みる（失敗すれば ERROR 経由のリトライが継続する）
        scheduleRetry(() => attemptStartRef.current?.());
      }
    }, STALL_CHECK_INTERVAL_MS);

    return () => clearInterval(timerId);
  }, [updateState, scheduleRetry]);

  // 「再試行」ボタン: プレイヤー内の再接続を即時実行する（STU-L4対応）。
  // ページ全体のリロードは映像以外（テスト回答状態・チャット・WebSocket）まで
  // 破棄して再構築する重い操作のため行わない。
  // attemptStart はインスタンスを破棄して新規作成するため、壊れた状態からも復旧できる。
  // プレイヤー未初期化（SDK読み込み失敗など attemptStartRef が無い）場合のみリロードにフォールバックする。
  const handleRetry = useCallback(() => {
    if (attemptStartRef.current) {
      retryDelayMsRef.current = 2500; // 手動再試行では自動リトライのバックオフをリセット
      attemptStartRef.current();
    } else {
      window.location.reload();
    }
  }, []);

  const isLectureEndedForOverlay = lectureLiveStatus === 'ended';
  const isLectureReadyForOverlay = lectureLiveStatus === 'ready';

  // オーバーレイの表示状態を管理
  // ready(講義前) または ended(講義終了) は常にオーバーレイを表示
  const showOverlayState =
    isLectureReadyForOverlay ||
    isLectureEndedForOverlay ||
    streamStatus === 'not_started' ||
    streamStatus === 'error' ||
    streamStatus === 'interrupted' ||
    streamStatus === 'ended';

  const pendingOverlayCopy =
    streamStatus === 'not_started' || isLectureReadyForOverlay
      ? getPendingOverlayCopy(lectureLiveStatus)
      : null;

  const overlayTitle =
    streamStatus === 'error'
      ? 'エラーが発生しました'
      : isLectureEndedForOverlay
        ? '配信が終了しました'
        : streamStatus === 'interrupted'
          ? '配信が中断されました'
          : pendingOverlayCopy
            ? pendingOverlayCopy.title
            : streamStatus === 'ended'
              ? '配信が終了しました'
              : '';

  const overlaySubtitle =
    streamStatus === 'error'
      ? errorMessage || 'エラーが発生しました。'
      : isLectureEndedForOverlay
        ? '配信が終了した可能性があります。再開される場合は「再試行」を押してください。'
        : streamStatus === 'interrupted'
          ? '通信が途切れた可能性があります。自動的に再接続しています。しばらく復旧しない場合は「再試行」を押してください。'
          : pendingOverlayCopy
            ? pendingOverlayCopy.subtitle
            : streamStatus === 'ended'
              ? '配信が終了した可能性があります。再開される場合は「再試行」を押してください。'
              : '';

  return (
    <>
      <video
        id="ivs-video"
        ref={videoRef}
        playsInline
        controls
        aria-label="ライブ配信動画プレイヤー"
      />
      {/* IVS Overlay */}
      {showOverlayState && (
        <div id="ivs-overlay" className="ivs-overlay" style={{ display: 'flex' }}>
          <div className="ivs-overlay-inner">
            <div id="ivs-overlay-title" className="ivs-overlay-title">
              {overlayTitle}
            </div>
            <div id="ivs-overlay-subtitle" className="ivs-overlay-subtitle">
              {overlaySubtitle}
            </div>
            <button id="ivs-retry-btn" className="ivs-retry-btn" type="button" onClick={handleRetry}>
              再試行
            </button>
          </div>
        </div>
      )}
    </>
  );
});

IVSPlayer.displayName = 'IVSPlayer';
